import { format, formatISO } from 'date-fns';

import {
  type AnimalEditValues,
  type AnimalRow,
  buildEditAnimalInput,
  type CreateAnimalProcedureInput,
  type EditAnimalInput,
  type ProcedureRequest,
} from '@/services/animal-management';
import type { AnimalPayload } from '@/services/animal-management/api';

export type BulkEditValues = {
  ownerId: string | null;
  yardId: string | null;
  penId: string | null;
  status: string;
  weight?: number;
  opuProgram: string;
  birthDate?: Date;
  // Calf section. Calves have their own OPU program option set.
  calfOpuProgram?: string;
  // Recipient section (#26669).
  recipientStatusId?: string;
  ocvStatusId?: string;
  client?: string;
  colorId?: string | null;
  lastDrugUsedId?: string | null;
  receivedDate?: Date;
};

const DONOR_TYPES = new Set(['Donor', 'LOPU Donor']);

/**
 * Splits the form into per-section changes and builds one PUT body per animal.
 * Donor-, recipient- and calf-section fields only go to animals of that type;
 * each body is then trimmed to what that animal's table can store.
 */
export function buildBulkEditInputs(
  animals: AnimalRow[],
  values: BulkEditValues,
): { inputs: EditAnimalInput[]; skipped: AnimalRow[] } {
  const common: Partial<AnimalEditValues> = {};
  if (values.ownerId) common.ownerId = values.ownerId;
  if (values.yardId) common.yardId = values.yardId;
  if (values.status === 'active' || values.status === 'inactive') {
    common.status = values.status;
  }
  if (typeof values.weight === 'number') common.weight = values.weight;

  const donor: Partial<AnimalEditValues> = {};
  if (values.opuProgram.trim()) donor.opuProgram = values.opuProgram.trim();

  const calf: Partial<AnimalEditValues> = {};
  if (values.birthDate) {
    calf.birthDate = format(values.birthDate, 'yyyy-MM-dd');
  }
  if (values.penId) calf.penId = values.penId;
  if (values.calfOpuProgram?.trim())
    calf.opuProgram = values.calfOpuProgram.trim();

  const recipient: Partial<AnimalEditValues> = {};
  if (values.recipientStatusId)
    recipient.recipientStatusId = values.recipientStatusId;
  if (values.ocvStatusId) recipient.ocvStatusId = values.ocvStatusId;
  if (values.client) recipient.client = values.client;
  if (values.colorId) recipient.colorId = values.colorId;
  if (values.lastDrugUsedId) recipient.lastDrugUsedId = values.lastDrugUsedId;
  if (values.receivedDate) {
    recipient.receivedDate = format(values.receivedDate, 'yyyy-MM-dd');
  }

  const inputs: EditAnimalInput[] = [];
  const skipped: AnimalRow[] = [];
  for (const animal of animals) {
    const changes = {
      ...common,
      ...(DONOR_TYPES.has(animal.animalType) ? donor : {}),
      ...(animal.animalType === 'Recipient' ? recipient : {}),
      ...(animal.animalType === 'Calf' ? calf : {}),
    };
    const { input } = buildEditAnimalInput(animal, changes);
    if (input) inputs.push(input);
    else skipped.push(animal);
  }
  return { inputs, skipped };
}

/**
 * One POST per animal per procedure. The performer is only sent when it is
 * someone other than the current user; otherwise the API defaults it to the
 * caller (#25588 Scenario 2).
 */
type ProcedureFormDetails = {
  time?: string;
  drugId?: string;
  dose?: number | null;
  unit?: string;
  weight?: number | null;
  pregnant?: 'Yes' | 'No' | '';
  ownerId?: string;
  technicianId?: string;
  diagnosisId?: string;
  temperature?: number | null;
};

/** Optional procedure datapoints (#40008); blanks are left out of the request. */
function procedureDetails(
  procedure: ProcedureFormDetails,
): Partial<CreateAnimalProcedureInput> {
  const text = (value?: string) => value?.trim() || undefined;
  const number = (value?: number | null) => value ?? undefined;
  const details: Partial<CreateAnimalProcedureInput> = {
    time: procedure.time ? Number(procedure.time) : undefined,
    drugId: text(procedure.drugId),
    dose: number(procedure.dose),
    unit: text(procedure.unit),
    weight: number(procedure.weight),
    pregnant:
      procedure.pregnant === 'Yes'
        ? 1
        : procedure.pregnant === 'No'
          ? 0
          : undefined,
    ownerId: text(procedure.ownerId),
    technicianId: text(procedure.technicianId),
    diagnosisId: text(procedure.diagnosisId),
    temperature: number(procedure.temperature),
  };
  return Object.fromEntries(
    Object.entries(details).filter(([, value]) => value !== undefined),
  );
}

export function buildProcedureRequests(
  animals: Pick<AnimalRow, 'id'>[],
  procedures: Array<
    {
      procedureCatalogId: string;
      description: string;
      enteredById: string;
      date?: Date;
    } & ProcedureFormDetails
  >,
  currentUserId: string | undefined,
): ProcedureRequest[] {
  return animals.flatMap((animal) =>
    procedures.map((procedure) => ({
      animalId: animal.id,
      input: {
        procedureCodeId: procedure.procedureCatalogId,
        procedureDate: formatISO(procedure.date!, { representation: 'date' }),
        description: procedure.description.trim() || undefined,
        performedById:
          procedure.enteredById && procedure.enteredById !== currentUserId
            ? procedure.enteredById
            : undefined,
        ...procedureDetails(procedure),
      },
    })),
  );
}

export const procedureSaveKey = (rowKey: string, animalId: string) =>
  `${rowKey}:${animalId}`;

export type PendingProcedureRequest = ProcedureRequest & { rowKey: string };

/**
 * Builds create requests for every (procedure row, animal) pair that has not
 * already been saved, so resubmitting after a partial failure does not create
 * duplicates of the rows that succeeded.
 */
export function buildPendingProcedureRequests(
  animals: Pick<AnimalRow, 'id'>[],
  procedures: Array<
    Parameters<typeof buildProcedureRequests>[1][number] & { rowKey: string }
  >,
  currentUserId: string | undefined,
  saved: ReadonlySet<string>,
): PendingProcedureRequest[] {
  return procedures.flatMap(({ rowKey, ...procedure }) =>
    buildProcedureRequests(animals, [procedure], currentUserId)
      .filter((r) => !saved.has(procedureSaveKey(rowKey, r.animalId)))
      .map((r) => ({ ...r, rowKey })),
  );
}

/**
 * Applies create results: returns the updated saved set, the rows now saved
 * for every animal, and the first failure message per row that still has
 * unsaved animals.
 */
export function applyProcedureResults(
  animals: Pick<AnimalRow, 'id'>[],
  rowKeys: string[],
  requests: PendingProcedureRequest[],
  results: AnimalPayload[],
  saved: ReadonlySet<string>,
) {
  const nextSaved = new Set(saved);
  const failures = new Map<string, string>();
  requests.forEach((request, i) => {
    const result = results[i];
    if (result?.success) {
      nextSaved.add(procedureSaveKey(request.rowKey, request.animalId));
    } else if (!failures.has(request.rowKey)) {
      failures.set(
        request.rowKey,
        result?.message ?? Object.values(result?.errors ?? {})[0] ?? '',
      );
    }
  });
  const completedRowKeys = rowKeys.filter((rowKey) =>
    animals.every((a) => nextSaved.has(procedureSaveKey(rowKey, a.id))),
  );
  return { saved: nextSaved, completedRowKeys, failures };
}
